import { ToClientCommand, ToServerCommand } from "../constants.js";
import { S2SEventContextService } from "../../s2s/s2s_event_context.js";
import { S2SEventOutbox } from "../../s2s/s2s_event_outbox.js";
import { CustomEventResponse } from "../data_structures/to_client/custom_event_response.js";
import { CustomEventRequest } from "../data_structures/to_server/custom_event_request.js";
import { ServerController } from "../server_controller.js";

export class CustomEventsController
{
  private static readonly CONNECT_TIMEOUT_MS = 4_000;
  private static readonly RESPONSE_TIMEOUT_MS = 12_000;
  private static readonly MAX_RETRY_DELAY_MS = 60_000;
  private static readonly OPTIMIZATION_EVENTS = new Set( [
    'tutorial_complete',
    'level_5_complete',
    'level_10_complete',
    'purchase',
  ] );
  private static readonly S2S_PLATFORMS = new Set( [ 'android' ] );

  private static server: ServerController | null = null;
  private static session_ready = false;
  private static is_flushing = false;
  private static in_flight_event_id: string | null = null;
  private static response_timer: ReturnType<typeof setTimeout> | null = null;
  private static flush_timer: ReturnType<typeof setTimeout> | null = null;

  public static register( server: ServerController ): void
  {
    this.server = server;
    this.markSessionUnavailable();
    server.on( ToClientCommand.EventResponse, reader =>
    {
      const response = new CustomEventResponse();
      response.deserialize( reader );
      this.handleEventResponse( response );
    } );
  }

  public static async sendCustomEvent( payload: Record<string, unknown> ): Promise<boolean>
  {
    if ( !this.server ) return false;

    const connected = await this.server.waitForConnectionWithTimeout( this.CONNECT_TIMEOUT_MS );
    if ( !connected ) return false;

    return this.server.sendRequest( new CustomEventRequest( payload ), ToServerCommand.Event );
  }

  public static queueS2SEvent(
    event_name: string,
    properties: Record<string, unknown> = {},
    options: { transaction_id?: string } = {},
  ): string | null
  {
    if ( !this.OPTIMIZATION_EVENTS.has( event_name ) )
    {
      console.warn( `[S2S] event is not enabled for optimization: ${event_name}` );
      return null;
    }

    const platform = String( window.Capacitor?.getPlatform?.() ?? 'web' ).toLowerCase();
    if ( !this.S2S_PLATFORMS.has( platform ) ) return null;

    const event = S2SEventContextService.createEvent( event_name, properties, options );
    if ( !S2SEventOutbox.enqueue( event ) ) return null;
    void this.flushOutbox();
    return event.event_id;
  }

  public static isSessionReady(): boolean
  {
    return this.session_ready;
  }

  public static getDebugStatus(): { session_ready: boolean; in_flight_event_id: string | null }
  {
    return { session_ready: this.session_ready, in_flight_event_id: this.in_flight_event_id };
  }

  public static retryOutboxNow(): void
  {
    S2SEventOutbox.makeAllReady();
    void this.flushOutbox();
  }

  public static markSessionReady(): void
  {
    this.session_ready = true;
    void this.flushOutbox();
  }

  public static markSessionUnavailable(): void
  {
    this.session_ready = false;
    this.is_flushing = false;
    this.in_flight_event_id = null;
    this.clearResponseTimer();
    this.clearFlushTimer();
  }

  private static handleEventResponse( response: CustomEventResponse ): void
  {
    const status = response.status?.trim().toLowerCase() ?? '';
    const accepted = status === 'ok';

    if ( accepted && response.event_id )
    {
      if ( response.rejected )
        console.warn( `[S2S] server dropped event ${response.event_id}${response.error ? `: ${response.error}` : ''}` );
      const was_in_flight = response.event_id === this.in_flight_event_id;
      const acknowledged = S2SEventOutbox.acknowledge( response.event_id );
      if ( was_in_flight )
      {
        this.in_flight_event_id = null;
        this.clearResponseTimer();
      }
      if ( acknowledged || was_in_flight ) void this.flushOutbox();
      return;
    }

    // Normal analytics responses may use either the current "ok" or legacy
    // "success" status, but only an explicit "ok" plus a matching event_id
    // is allowed to delete a durable S2S event.
    if ( accepted || status === 'success' ) return;

    if ( status )
      console.warn( `[WebSocket] event response status: ${status}${response.error ? ` (${response.error})` : ''}` );

    if ( this.in_flight_event_id && ( !response.event_id || response.event_id === this.in_flight_event_id ) )
    {
      this.in_flight_event_id = null;
      this.clearResponseTimer();
      this.scheduleNextFlush();
    }
  }

  private static async flushOutbox(): Promise<void>
  {
    if ( this.is_flushing || this.in_flight_event_id || !this.session_ready || !this.server ) return;
    this.is_flushing = true;

    try
    {
      const entry = S2SEventOutbox.peekReady();
      if ( !entry )
      {
        this.scheduleNextFlush();
        return;
      }

      this.in_flight_event_id = entry.event.event_id;
      const sent = this.server.sendRequest(
        new CustomEventRequest( entry.event.payload, entry.event ),
        ToServerCommand.Event,
      );

      const retry_delay = Math.min(
        this.MAX_RETRY_DELAY_MS,
        5_000 * Math.pow( 2, Math.min( entry.attempts, 4 ) ),
      );
      const next_attempt_at = Date.now() + Math.max( this.RESPONSE_TIMEOUT_MS, retry_delay );
      S2SEventOutbox.recordAttempt( entry.event.event_id, next_attempt_at );

      if ( !sent )
      {
        this.in_flight_event_id = null;
        this.scheduleNextFlush();
        return;
      }

      this.clearResponseTimer();
      this.response_timer = setTimeout( () =>
      {
        if ( this.in_flight_event_id === entry.event.event_id )
          this.in_flight_event_id = null;
        this.response_timer = null;
        this.scheduleNextFlush();
      }, this.RESPONSE_TIMEOUT_MS );
    }
    finally
    {
      this.is_flushing = false;
    }
  }

  private static scheduleNextFlush(): void
  {
    if ( !this.session_ready || this.in_flight_event_id ) return;
    const delay = S2SEventOutbox.millisecondsUntilNext();
    if ( delay === null ) return;

    this.clearFlushTimer();
    this.flush_timer = setTimeout( () =>
    {
      this.flush_timer = null;
      void this.flushOutbox();
    }, delay );
  }

  private static clearResponseTimer(): void
  {
    if ( this.response_timer ) clearTimeout( this.response_timer );
    this.response_timer = null;
  }

  private static clearFlushTimer(): void
  {
    if ( this.flush_timer ) clearTimeout( this.flush_timer );
    this.flush_timer = null;
  }
}
